import { isWebTaskEventStreamConnected } from '../platform/webBridge';
import { useEffect } from 'react';
import { useTaskStore } from '../stores/useTaskStore';
import { useResultStore } from '../stores/useResultStore';
import { createCoalescedRefresh } from '../platform/coalescedRefresh';
import type { TaskStatus } from '../types';

export function useTaskMonitor() {
  const updateTaskStatus = useTaskStore(state => state.updateTaskStatus);
  const updateTaskProgress = useTaskStore(state => state.updateTaskProgress);
  const updateTaskOutputStatus = useTaskStore(state => state.updateTaskOutputStatus);
  const loadTasks = useTaskStore(state => state.loadTasks);
  const loadResults = useResultStore(state => state.loadResults);

  useEffect(() => {
    const refresh = createCoalescedRefresh(async () => {
      if (document.visibilityState !== 'visible') return;
      await Promise.all([loadTasks(), loadResults()]);
      window.dispatchEvent(new Event('task-state-refreshed'));
    });
    const handleProgress = (data: { taskId: string; progress: number; message: string }) => {
      updateTaskProgress(data.taskId, data.progress, data.message);
    };

    const handleStatusChange = (data: { taskId: string; status: string; errorMessage?: string }) => {
      updateTaskStatus(data.taskId, data.status as TaskStatus, data.errorMessage);
    };

    const handleOutputReady = (data: {
      taskId: string;
      outputId: string;
      outputStatus?: 'pending' | 'running' | 'success' | 'failed';
      outputErrorMessage?: string;
      outputUpdatedAt?: string;
    }) => {
      if (data.outputStatus) {
        updateTaskOutputStatus(
          data.taskId,
          data.outputId,
          data.outputStatus,
          data.outputErrorMessage,
          data.outputUpdatedAt,
        );
      }
      if (!data.outputStatus || data.outputStatus === 'success' || data.outputStatus === 'failed') {
        refresh.request();
      }
    };

    const handleQueueComplete = (data?: { reason?: string }) => {
      refresh.request(data?.reason === 'event-stream-connected');
    };

    if (typeof window === 'undefined' || !window.electron || typeof window.electron.on !== 'function') {
      console.warn('[useTaskMonitor] window.electron.on 不可用，跳过任务状态监听');
      return;
    }

    const progressListener = window.electron.on(window.IPC_CHANNELS.TASK_PROGRESS, handleProgress);
    const statusListener = window.electron.on(window.IPC_CHANNELS.TASK_STATUS_CHANGE, handleStatusChange);
    const outputListener = window.electron.on(window.IPC_CHANNELS.TASK_OUTPUT_READY, handleOutputReady);
    const queueCompleteListener = window.electron.on(window.IPC_CHANNELS.TASK_QUEUE_COMPLETE, handleQueueComplete);
    const refreshTasks = () => {
      if (document.visibilityState === 'visible') refresh.request();
    };
    let lastFallback = Date.now();
    const refreshTimer = window.setInterval(() => {
      const interval = window.CLOUD_RUNTIME && isWebTaskEventStreamConnected() ? 60_000 : 10_000;
      if (Date.now() - lastFallback < interval) return;
      lastFallback = Date.now();
      refreshTasks();
    }, 10_000);
    window.addEventListener('focus', refreshTasks);
    document.addEventListener('visibilitychange', refreshTasks);

    return () => {
      refresh.dispose();
      window.clearInterval(refreshTimer);
      window.removeEventListener('focus', refreshTasks);
      document.removeEventListener('visibilitychange', refreshTasks);
      if (!window.electron || typeof window.electron.off !== 'function') return;
      window.electron.off(window.IPC_CHANNELS.TASK_PROGRESS, progressListener);
      window.electron.off(window.IPC_CHANNELS.TASK_STATUS_CHANGE, statusListener);
      window.electron.off(window.IPC_CHANNELS.TASK_OUTPUT_READY, outputListener);
      window.electron.off(window.IPC_CHANNELS.TASK_QUEUE_COMPLETE, queueCompleteListener);
    };
  }, [loadResults, loadTasks, updateTaskOutputStatus, updateTaskProgress, updateTaskStatus]);
}
